<script setup lang="ts">
import {
  useToast,
  AtomLink,
  AtomText,
  AtomInput,
  AtomButton,
  SatFormControl,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import { ref, computed, onBeforeMount } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  isAuthRepoError,
  isLoyaltyRepoError,
} from "@chatfood/dbe-client/errors";
import type { IAuthManagerProps } from "./props";
import type { IAuthManagerEmits } from "./emits";
import { useBugReporter, useDirectBackend, useFeatureFlag } from "#imports";
import { SignInCustomer } from "@/components/sign-in-customer";
import { SignUpCustomer } from "@/components/sign-up-customer";
import { useBusinessStore } from "~/stores/business";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useDiscountsStore } from "~/stores/discounts";
import { useGuestStore } from "~/stores/guest";
import { useLocale } from "~/composables/use-locale";

const props = withDefaults(defineProps<IAuthManagerProps>(), {
  allowProceedAsGuest: false,
});

const emits = defineEmits<IAuthManagerEmits>();

const { t } = useLocale();
const { toastError } = useToast();
const { report } = useBugReporter();

const OTP_THROTTLE_SECONDS = 30;

const css = bemBuilder("auth-manager");

const business = useBusinessStore();
const customer = useCustomerStore();
const basket = useBasketStore();
const discounts = useDiscountsStore();
const guest = useGuestStore();
const featureMatrix = useFeatureMatrixStore();
const { isEnabled } = useFeatureFlag();

const email = ref("");
const maskedNumber = ref("");
const flow = ref<"signin" | "signup">();
const isCheckingSso = ref(true);
const skipOtpViaSelection = ref(false);

const onUserDetected = (scrubbedPhoneNumber: string) => {
  maskedNumber.value = scrubbedPhoneNumber;
  flow.value = "signin";
};

const onUserNotDetected = () => {
  flow.value = "signup";
};

const lookupErrorMessage = ref<string>();
const isLookingUp = ref(false);

const onEmailInput = (value: string) => {
  email.value = value;
};

const { authRepo, loyaltyRepo } = useDirectBackend();

const onLookup = async () => {
  lookupErrorMessage.value = undefined;

  try {
    isLookingUp.value = true;

    const res = await authRepo.emailLookup({ email: email.value });

    if (res) {
      onUserDetected(res.maskedPhoneNumber);
    } else {
      onUserNotDetected();
    }
  } catch (error: unknown) {
    if (isAuthRepoError(error) && error.isValidation() && error.hasMeta()) {
      lookupErrorMessage.value = error.meta.email;
      return;
    }

    lookupErrorMessage.value = t("components.auth_manager.error_unknown");

    report(error);
  } finally {
    isLookingUp.value = false;
  }
};

const onAuthComplete = async () => {
  guest.reset();

  try {
    await Promise.allSettled([
      joinLoyaltyProgram(),
      syncCustomerLoyaltyProfile(),
      refreshDiscountList(),
      validateAppliedDiscount(),
      trackGoogleConversion(),
    ]);

    basket.revalidate();
  } finally {
    emits("complete");
    onReset();
  }
};

const fetchProvider = async () => {
  try {
    const res = await loyaltyRepo.getSSoProvider({
      businessId: business.id,
      channel: business.salesChannel,
    });

    if (!res.ssoRedirectUrl) {
      isCheckingSso.value = false;
      return;
    }

    const url = new URL(res.ssoRedirectUrl);
    url.searchParams.set("returnUrl", window.location.href);
    window.location.href = url.toString();
  } catch {
    // @TODO: add a report to sentry once the endpoint is available
    isCheckingSso.value = false;
  }
};

onBeforeMount(async () => {
  skipOtpViaSelection.value = await isEnabled("direct.skipOtpViaSelection");

  fetchProvider();
});

const joinLoyaltyProgram = async () => {
  try {
    await loyaltyRepo.joinProgram({ businessId: business.id });
  } catch (error) {
    if (isLoyaltyRepoError(error) && error.isNotFound()) {
      return;
    }

    report(error);
  }
};

const syncCustomerLoyaltyProfile = async () => {
  if (!business.id || !basket.outletId || !basket.salesChannel) {
    return;
  }

  try {
    await customer.syncLoyalty(
      business.id,
      basket.salesChannel,
      basket.outletId,
    );
  } catch {
    //
  }
};

const trackGoogleConversion = async () => {
  if (basket.outletId && basket.salesChannel) {
    await customer.trackGoogleConversion(basket.outletId, basket.salesChannel);
  }
};

const onReset = () => {
  email.value = "";
  maskedNumber.value = "";
  flow.value = undefined;
};

const refreshDiscountList = async () => {
  if (!discounts.isLoaded || !basket.isLoaded) return;

  await discounts.load(basket.businessId, basket.outletId, basket.orderingMode);
};

const validateAppliedDiscount = async () => {
  if (!basket.isLoaded || !basket.discountCode) return;

  const discountCode = basket.discountCode;

  await basket.revalidateDiscount();

  if (discountCode === basket.discountCode) return;

  toastError(
    t("components.auth_manager.discount_code_is_ineligible", {
      discountCode,
    }),
  );
};

const displayGuestOption = computed(() => {
  return (
    props.allowProceedAsGuest &&
    featureMatrix.isToggleEnabled("allow-guest-checkout")
  );
});

const onGuest = () => {
  guest.setStartedFlow(true);
  emits("complete");
};
</script>

<template>
  <div :class="css('')">
    <AtomLoadingSpinner v-if="isCheckingSso" :class="css('loading')" />
    <form
      v-else-if="!flow && !isCheckingSso"
      data-test="lookup"
      :class="css('lookup')"
    >
      <div :class="css('lookup-header')">
        <AtomText type="h3">
          {{ t("components.auth_manager.lookup_title") }}
        </AtomText>
        <AtomText type="body" color="color-base-40">
          {{ t("components.auth_manager.lookup_subtitle") }}
        </AtomText>
      </div>
      <SatFormControl
        :label="t('components.auth_manager.lookup_email_label')"
        :error-message="lookupErrorMessage"
      >
        <AtomInput
          :value="email"
          :placeholder="t('components.auth_manager.lookup_email_placeholder')"
          :has-error="Boolean(lookupErrorMessage)"
          type="email"
          @change="onEmailInput"
        />
      </SatFormControl>
      <AtomButton
        :class="css('lookup-cta')"
        :is-loading="isLookingUp"
        :wide="true"
        type="submit"
        size="large"
        @click="onLookup"
      >
        {{ t("components.auth_manager.lookup_cta") }}
      </AtomButton>
      <div v-if="displayGuestOption" data-test="guest" :class="css('guest')">
        <AtomLink to="" @click.prevent="onGuest">
          {{ t("components.auth_manager.guest_cta") }}
        </AtomLink>
      </div>
    </form>
    <SignInCustomer
      v-if="flow === 'signin'"
      :email="email"
      :phone-number="maskedNumber"
      :throttle-seconds="OTP_THROTTLE_SECONDS"
      :skip-otp-selector="skipOtpViaSelection"
      @success="onAuthComplete"
      @failure="onReset"
    />
    <SignUpCustomer
      v-if="flow === 'signup'"
      :email="email"
      :skip-otp-selector="skipOtpViaSelection"
      :throttle-seconds="OTP_THROTTLE_SECONDS"
      @success="onAuthComplete"
      @failure="onReset"
    />
  </div>
</template>

<style lang="scss" scoped>
.auth-manager {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 375px;

  @media (width < $breakpoint-sm) {
    width: initial;
    padding: var(--spacing-lg);
  }

  &__loading {
    margin: var(--spacing-3xl) auto;
  }

  &__lookup {
    display: flex;
    flex-direction: column;
  }

  &__lookup-header {
    margin-bottom: var(--spacing-lg);
  }

  &__lookup-cta {
    margin-top: var(--spacing-lg);
  }

  &__guest {
    text-align: center;
    margin-top: var(--spacing-lg);
  }
}
</style>
